import { useCallback, useContext } from 'react'

import { useGlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { Shapes } from '@editor/interfaces/shape'
import { ERROR_PROCESS, processErrorHandler } from '@editor/services/ErrorHandler'
import { getShapes } from '@editor/services/InspectionArea'
import { RootState, useAppDispatch } from '@editor/store/app'
import { useSelector } from 'react-redux'

import { setShapes } from '../editor/store/shapes'

export const useShapes = (fetchAlignedAreas = false) => {
  // Context
  const { getAccessToken } = useContext(UserContext)
  const { showErrorModal } = useGlobalModalContext()

  // Store
  const dispatch = useAppDispatch()
  const project = useSelector((state: RootState) => state.page.project)
  const inspectionArea = useSelector((state: RootState) => state.page.inspectionArea)
  const connectedAreas = useSelector((state: RootState) => state.page.connectedAreas)

  /**
   * Fetch all shapes
   */
  const fetchShapes = useCallback(async () => {
    if (!project || !inspectionArea) return false

    const token = await getAccessToken()
    if (!token) return false

    try {
      let shapes: Shapes

      // -- Fetch all items including aligned areas
      if (fetchAlignedAreas) {
        if (!connectedAreas || Object.keys(connectedAreas).length === 0) return false

        // Get all area IDs, including aligned areas
        const areaIds = connectedAreas.map(({ current: { area } }) => area.inspection_area_id)
        const results = await Promise.all(areaIds.map((areaId) => getShapes(token, project.project_id, areaId)))

        // Merge results from all areas
        shapes = results.reduce<Shapes>(
          (acc, curr) => ({
            polygons: [...(acc.polygons || []), ...(curr?.polygons || [])],
            cylinders: [...(acc.cylinders || []), ...(curr?.cylinders || [])],
          }),
          { polygons: [], cylinders: [] },
        )
      } else {
        const results = await getShapes(token, project.project_id, inspectionArea.inspection_area_id, showErrorModal)
        if (!results) return false
        shapes = results
      }

      dispatch(setShapes(shapes))
    } catch (err) {
      processErrorHandler(err, ERROR_PROCESS.GET_SHAPES, showErrorModal)
      return false
    }

    return true
  }, [project, inspectionArea, connectedAreas, fetchAlignedAreas, getAccessToken, showErrorModal, dispatch])

  return {
    fetchShapes,
  }
}
